import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Download, Mail } from 'lucide-react';
import { NotesEditor } from './NotesEditor';
import { DecisionEmailDialog } from './DecisionEmailDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-700',
  approved: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-gray-900 break-words">{value || '—'}</p>
  </div>
);

export const ApplicationCard = ({ app, onStatusChange, onSaveNotes }) => {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const status = app.status || 'pending';
  const submitted = app.timestamp ? new Date(app.timestamp).toLocaleDateString() : '—';

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100" data-testid={`application-card-${app.id}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`application-toggle-${app.id}`}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
      >
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{app.full_name}</p>
          <p className="text-sm text-gray-600 truncate">{app.school_name} · {app.program} · Submitted {submitted}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span data-testid={`application-status-badge-${app.id}`} className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS_STYLES[status]}`}>
            {status}
          </span>
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 p-5 space-y-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Email" value={app.email} />
            <Field label="Phone" value={app.phone} />
            <Field label="Expected Graduation" value={app.expected_graduation} />
            <Field label="Requested Dates" value={`${app.requested_start_date} to ${app.requested_end_date}`} />
            <Field label="Hours Requested" value={app.hours_requested} />
            <Field label="Status Updated By" value={app.status_updated_by} />
          </div>
          <Field label="Learning Objectives" value={app.learning_objectives} />
          <div className="flex flex-wrap gap-3">
            {app.has_resume && (
              <a href={`${API}/preceptorship/${app.id}/file/resume`} data-testid={`download-resume-${app.id}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors">
                <Download size={16} /> Résumé
              </a>
            )}
            {app.has_enrollment_proof && (
              <a href={`${API}/preceptorship/${app.id}/file/enrollment_proof`} data-testid={`download-enrollment-${app.id}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors">
                <Download size={16} /> Enrollment Proof
              </a>
            )}
          </div>
          <NotesEditor app={app} onSave={onSaveNotes} />
          {app.decision_email_sent && (
            <p className="text-sm text-gray-600 flex items-center gap-2" data-testid={`decision-email-sent-${app.id}`}>
              <Mail size={16} /> {app.decision_email_sent.status === 'approved' ? 'Approval' : 'Decline'} email sent
              {' '}{new Date(app.decision_email_sent.at).toLocaleDateString()} by {app.decision_email_sent.by}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {['pending', 'approved', 'declined'].map((s) => (
              <button key={s} onClick={() => (s === 'pending' ? onStatusChange(app.id, s, false) : setDecision(s))} disabled={s === status}
                data-testid={`set-status-${s}-${app.id}`}
                className="capitalize text-sm font-semibold px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:bg-gray-900 disabled:text-white disabled:border-gray-900">
                {s === 'pending' ? 'Mark Pending' : s === 'approved' ? 'Approve' : 'Decline'}
              </button>
            ))}
          </div>
        </div>
      )}
      <DecisionEmailDialog app={app} status={decision} onClose={() => setDecision(null)} onConfirm={onStatusChange} />
    </div>
  );
};
